<template>
  <div
    class="propeller-company-switcher relative inline-block"
    ref="containerRef"
    :data-open="isOpen ? 'true' : 'false'"
  >
    <button
      type="button"
      aria-haspopup="listbox"
      :aria-label='getLabel("switchCompanyAriaLabel", "Switch company")'
      :class="cn(
        'propeller-company-switcher__trigger flex items-center gap-2 rounded-[var(--radius-control)] px-3 py-1.5 text-sm font-medium transition-colors text-inherit',
        triggerClassName,
      )"
      @click="async (event) => toggleDropdown()"
      :aria-expanded="isOpen"
    >
      <span
        aria-hidden="true"
        :class="`propeller-company-switcher__icon icon-${getIcon()} flex-shrink-0`"
      ></span
      ><span class="propeller-company-switcher__label truncate max-w-[160px]">{{
        getActiveCompanyName()
      }}</span
      ><span
        aria-hidden="true"
        :class="`propeller-company-switcher__chevron flex-shrink-0 transition-transform duration-200 ${
          isOpen ? 'rotate-180' : 'rotate-0'
        }`"
        ><svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2 4l4 4 4-4"></path></svg
      ></span>
    </button>
    <template v-if="isOpen">
      <ul
        role="listbox"
        :aria-label='getLabel("companiesAriaLabel", "Companies")'
        class="propeller-company-switcher__dropdown absolute left-0 top-full z-[60] mt-1 min-w-[220px] rounded-[var(--radius-control)] border border-border bg-popover text-popover-foreground shadow-lg py-1 animate-in fade-in zoom-in-95 duration-150"
      >
        <template :key="String(company.companyId)" v-for="(company, index) in getCompanies()">
          <li
            role="option"
            :aria-selected="isActive(company)"
            @click="async (event) => selectCompany(company)"
            :class="`propeller-company-switcher__option flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer transition-colors hover:bg-accent hover:text-accent-foreground ${
              isActive(company) ? 'font-semibold text-primary' : 'font-normal text-foreground'
            }`"
          >
            <span class="propeller-company-switcher__option-name flex-1 truncate">{{ company.name }}</span>
            <template v-if="isActive(company)">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                class="propeller-company-switcher__option-check flex-shrink-0 w-4 h-4 text-primary"
              >
                <path d="M2.5 8l4 4 7-7"></path>
              </svg>
            </template>
          </li>
        </template>
      </ul>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useInfraProps } from '../composables/vue/useInfraProps';

import { Contact, Company } from '@propeller-commerce/propeller-sdk-v2';
import { getLabel as _getLabel } from '@propeller-commerce/propeller-v2-core-ui';
import { cn } from '../composables/shared/utils/cn';

export interface CompanySwitcherProps {
  /** The contact to whom the companies are assigned. Default company is user.company, all companies are in user.companies. Resolved from `<PropellerProvider>` when omitted. */
  user?: Contact;

  /** Icon identifier for the company switcher trigger button. @default 'default-company-switch-icon' */
  icon?: string;

  /** Currently selected company ID (from CompanyContext). Syncs the switcher with external state. */
  selectedCompanyId?: number;

  /** Callback fired when the user selects a company. */
  onCompanyChange: (company: Company) => void;

  /** Translated labels keyed by the slugs used inside the component (see
   * `getLabel` calls). Missing keys fall back to the English defaults. */
  labels?: Record<string, string>;

  /** Additional class name for the switcher's trigger button. */
  triggerClassName?: string;
}
interface CompanySwitcherState {
  isOpen: boolean;
  activeCompanyId: number | null;
  getCompanies: () => Company[];
  getActiveCompany: () => Company | null;
  getActiveCompanyName: () => string;
  getIcon: () => string;
  isActive: (company: Company) => boolean;
  toggleDropdown: () => void;
  selectCompany: (company: Company) => void;
}

const props = defineProps<CompanySwitcherProps>();
// Resolve `user` from the propellerVue plugin scope when the consumer doesn't
// pass it explicitly. AppHeader / chrome surfaces typically rely on the
// provider rather than wiring user through every prop.
const infra = useInfraProps(props);
const isOpen = ref<CompanySwitcherState['isOpen']>(false);
const activeCompanyId = ref<CompanySwitcherState['activeCompanyId']>(null);

const containerRef = ref<HTMLDivElement | null>(null);

watch(
  () => [isOpen.value],
  () => {
    if (!isOpen.value) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.value && !(containerRef.value as any).contains(e.target)) {
        isOpen.value = false;
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  },
  { immediate: true }
);
function getLabel(key: string, fallback: string): string {
  return _getLabel(props.labels, key, fallback);
}
function getCompanies(): ReturnType<CompanySwitcherState['getCompanies']> {
  const user = (infra.user ?? props.user) as Contact | undefined;
  if (!user) return [];
  // sanitizeUser in AuthContext is not recursive, so CompaniesResponse fields
  // may still have their raw _items key instead of the getter-based items.
  const companiesRaw = user.companies as any;
  const items = (companiesRaw?.items ?? companiesRaw?._items) as Company[] | undefined;
  if (Array.isArray(items) && items.length > 0) {
    return items;
  }
  const defaultCompany = user.company;
  if (defaultCompany) {
    return [defaultCompany];
  }
  return [];
}
function getActiveCompany(): ReturnType<CompanySwitcherState['getActiveCompany']> {
  const idToUse = activeCompanyId.value ?? (props.selectedCompanyId as number | undefined) ?? null;
  if (idToUse !== null) {
    const companies = getCompanies();
    const found = companies.find((c: Company) => c.companyId === idToUse);
    return found ?? null;
  }
  const user = (infra.user ?? props.user) as Contact | undefined;
  return (user?.company as Company | undefined) ?? null;
}
function getActiveCompanyName(): ReturnType<CompanySwitcherState['getActiveCompanyName']> {
  const company = getActiveCompany();
  return company ? company.name : 'Select company';
}
function getIcon(): ReturnType<CompanySwitcherState['getIcon']> {
  return props.icon ?? 'default-company-switch-icon';
}
function isActive(company: Company): ReturnType<CompanySwitcherState['isActive']> {
  const active = getActiveCompany();
  return active !== null && active.companyId === company.companyId;
}
function toggleDropdown(): ReturnType<CompanySwitcherState['toggleDropdown']> {
  isOpen.value = !isOpen.value;
}
function selectCompany(company: Company): ReturnType<CompanySwitcherState['selectCompany']> {
  activeCompanyId.value = company.companyId;
  isOpen.value = false;
  props.onCompanyChange(company);
}
</script>
